"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.HAIKU_MICRO_USD_PER_THOUSAND_OUTPUT = exports.HAIKU_MICRO_USD_PER_THOUSAND_INPUT = exports.JEV_MICRO_USD_PER_THOUSAND_TOKENS = exports.JEV_EVENTS = exports.correctJevSchema = exports.JEV_CORRECTION_NOTE_MAX = exports.JEV_FLAG_CORRECTION_CODES = exports.JEV_PART_CORRECTION_CODES = exports.JEV_CORRECTION_LABELS = exports.JEV_CORRECTION_CODES = exports.JEV_ENGLISH_CHIPS = exports.JEV_ENGLISH_LABELS = exports.JEV_ENGLISH = exports.JEV_TRANSFERABLE_AT = exports.JEV_GAP_AT = exports.JEV_OWNERSHIP_LABELS = exports.JEV_OWNERSHIP = exports.JEV_ASSESSABLE_LEVEL = exports.JEV_LEVEL_LABELS = exports.JEV_BETTER_WEIGHT = exports.JEV_MUST_WEIGHT = exports.JEV_MEETS_AT = exports.JEV_EVIDENCE_CREDIT = exports.JEV_EVIDENCE_LABELS = exports.JEV_EVIDENCE = exports.RED_FLAG_ASKS = exports.UNFAIR_ASK_BODY = exports.UNFAIR_ASK_TITLES = exports.runJevAskSchema = exports.jevAskPartSchema = exports.JEV_ICP_PARTS_MAX = exports.JEV_PARTS_MAX = exports.JEV_PART_MAX_LENGTH = exports.JEV_PART_MIN_LENGTH = exports.JEV_ASK_MAX_LENGTH = void 0;
exports.splitAsk = splitAsk;
exports.unfairAskKind = unfairAskKind;
exports.jevEvidenceOf = jevEvidenceOf;
exports.jevGradingOf = jevGradingOf;
exports.jevLevelOf = jevLevelOf;
exports.jevMatchOf = jevMatchOf;
exports.showsAllMusts = showsAllMusts;
exports.jevEvidenceSummaryOf = jevEvidenceSummaryOf;
exports.jevEnglishOf = jevEnglishOf;
exports.jevGapOf = jevGapOf;
exports.hasJevFlags = hasJevFlags;
exports.compareJevResults = compareJevResults;
exports.jevCostLabel = jevCostLabel;
exports.jevCentsLabel = jevCentsLabel;
const zod_1 = require("zod");
// ADR 0050, as amended by ADR 0051 — Jev (TypeSafe AI's System One model) on RNK-01. An ask is split here into
// parts; Jev grades, per CV and part, how strong the CV's evidence is and points at the lines that show it; this file
// turns those grades into the order. Haiku only puts what Jev found into words. Jev only re-orders: the criteria's
// score, stars and tab never move (ADR 0049). Shared, so the browser refuses an unfair ask at once and the server
// refuses it again.
exports.JEV_ASK_MAX_LENGTH = 600;
exports.JEV_PART_MIN_LENGTH = 2;
exports.JEV_PART_MAX_LENGTH = 200;
exports.JEV_PARTS_MAX = 6;
/** ADR 0061: an ideal candidate profile's ask carries up to ten lines; the ad-hoc ask stays at JEV_PARTS_MAX. */
exports.JEV_ICP_PARTS_MAX = 10;
exports.jevAskPartSchema = zod_1.z.object({
    text: zod_1.z.string().trim().min(exports.JEV_PART_MIN_LENGTH).max(exports.JEV_PART_MAX_LENGTH, `Keep each part under ${exports.JEV_PART_MAX_LENGTH} characters — split a long one in two.`),
    isMust: zod_1.z.boolean(),
});
exports.runJevAskSchema = zod_1.z.object({
    text: zod_1.z.string().trim().min(exports.JEV_PART_MIN_LENGTH, 'Say who you want.').max(exports.JEV_ASK_MAX_LENGTH),
    parts: zod_1.z.array(exports.jevAskPartSchema).min(1, 'Say who you want.').max(exports.JEV_PARTS_MAX, `Jev checks up to ${exports.JEV_PARTS_MAX} parts at once.`),
});
// ---- Splitting an ask (ADR 0051 §3) ----
/** Openings that say nothing about the person: "hey Jev, I want to find the guy who …", "I need some with …". */
const LEAD_INS = [
    /^(?:hey|hi|hello)\b[\s,!]*(?:jev\b[\s,!]*)?/i,
    /^(?:i\s+(?:want|need|am\s+looking\s+for)(?:\s+to\s+(?:find|see|have|hire))?|we\s+(?:want|need)|find(?:\s+me)?|show(?:\s+me)?|get(?:\s+me)?|looking\s+for|search\s+for)\b\s*/i,
    /^(?:(?:the|a|an|any|all|some)?\s*(?:guys?|persons?|people|candidates?|applicants?|some\s?one|some\s?body|any\s?one|ones?)\b|some\s+(?=(?:with|who|that)\b))\s*/i,
    /^(?:who|that|with|which)\b\s*/i,
];
/** "must be", "should", "has to", "to" before the requirement itself: "able to use Revit". */
const MODAL_LEAD = /^(?:(?:must|should|has\s+to|have\s+to|needs?\s+to|is\s+required\s+to|are\s+required\s+to)\s+(?:be\s+)?|to\s+)/i;
/** A part that starts with one of these is a "Better if"; the words themselves are dropped. */
const BETTER_IF_LEAD = /^(?:ideally|preferably|preferred|better\s+if|bonus(?:\s+if)?|nice\s+to\s+have|a\s+plus\s+if|if\s+possible|(?:it\s+)?would\s+be\s+(?:good|nice|great)\s+if|if)\b[\s:,-]*/i;
/** …or ends with one: "other tools, that is fine and better", "Revit would be a plus". */
const BETTER_IF_TRAIL = /(?:^|[\s,]+)(?:(?:(?:that|it|this)(?:['’]s|\s+is|\s+would\s+be|\s+will\s+be)?|would\s+be|is)\s+)?(?:(?:(?:fine|ok|okay|good|great|nice)\s+)?(?:and\s+)?(?:even\s+)?(?:better|a\s+plus|a\s+bonus|preferred|preferable|nice\s+to\s+have|welcome)|fine|ok|okay|if\s+possible|if\s+any)$/i;
/** Words that start a new requirement: the piece is split before them, and they are dropped. */
const CONNECTOR = String.raw `but|because|also|as\s+well\s+as|additionally|however|although|though|whereas`;
// ADR 0051 §3 splits at "plus", but "a plus" is a Better if cue (BETTER_IF_TRAIL), so never after "a" (ADR 0058).
const CONNECTOR_SPLIT = new RegExp(String.raw `(?<!\ba\s*)\s+(?=(?:${CONNECTOR}|plus)\b)`, 'i');
const CONNECTOR_LEAD = new RegExp(String.raw `^(?:${CONNECTOR}|plus)\b[\s,]*`, 'i');
/** A new "who …", "must …", "should …", "I need …" clause in the middle of a piece starts a new part. */
const CLAUSE_SPLIT = /\s+(?=(?:who|must|should|has\s+to|have\s+to|needs?\s+to|i\s+need|i\s+want|we\s+need)\b)/i;
/** "Tally and Excel" or "Mushak 6.3 and 9.1" stay one part; "has done VAT returns and uses Tally" becomes two. */
const AND_SPLIT = /\s+(?:and|&)\s+/i;
/** Marks a dot that ends a sentence, as a line break; "B.Sc.", "etc." and "6.3" keep their dots. */
const SENTENCE_DOT = /(\S+)\.(?=\s|$)/g;
const ABBREVIATION_MAX = 3;
function wordCount(text) {
    return text.split(/\s+/).filter(Boolean).length;
}
function withoutLeadIn(text) {
    let rest = text.trim();
    for (const pattern of LEAD_INS)
        rest = rest.replace(pattern, '');
    return rest.trim();
}
function sentencesOf(text) {
    const marked = text.replace(SENTENCE_DOT, (whole, word) => (word.includes('.') || word.replace(/[^\p{L}\p{N}]/gu, '').length <= ABBREVIATION_MAX ? whole : `${word}\n`));
    return marked.split(/[,;!?\n]+/);
}
/** A piece split before its connectors and new clauses — only where the side before has two words or more. */
function clausesOf(piece) {
    return piece
        .split(CONNECTOR_SPLIT)
        .map((part) => part.replace(CONNECTOR_LEAD, ''))
        .flatMap((part) => {
        const halves = part.split(CLAUSE_SPLIT);
        const joined = [];
        for (const half of halves) {
            const last = joined.at(-1);
            if (last !== undefined && wordCount(last) < 2)
                joined[joined.length - 1] = `${last} ${half}`;
            else
                joined.push(half);
        }
        return joined;
    });
}
function splitAtAnd(piece) {
    const halves = piece.split(AND_SPLIT);
    const joined = [];
    for (const half of halves) {
        const last = joined.at(-1);
        if (last !== undefined && (wordCount(last) < 2 || wordCount(half) < 2))
            joined[joined.length - 1] = `${last} and ${half}`;
        else
            joined.push(half);
    }
    return joined;
}
function partOf(piece) {
    const trimmed = withoutLeadIn(piece.trim().replace(/[.!?:]+$/, '')).replace(MODAL_LEAD, '');
    const isBetter = BETTER_IF_LEAD.test(trimmed) || BETTER_IF_TRAIL.test(trimmed);
    const text = isBetter ? trimmed.replace(BETTER_IF_LEAD, '').replace(BETTER_IF_TRAIL, '').replace(MODAL_LEAD, '') : trimmed;
    return { text: text.trim(), isMust: !isBetter };
}
/**
 * ADR 0051 §3: the parts of an ask, in the order typed, no repeats. Nothing is cut: a part over JEV_PART_MAX_LENGTH
 * or a seventh part is returned as it is, and the screen says so rather than dropping words silently.
 */
function splitAsk(text) {
    const parts = [];
    const seen = new Set();
    for (const piece of sentencesOf(withoutLeadIn(text)).flatMap(clausesOf).flatMap(splitAtAnd)) {
        const part = partOf(piece);
        const key = part.text.toLowerCase();
        if (part.text.length < exports.JEV_PART_MIN_LENGTH || seen.has(key))
            continue;
        seen.add(key);
        parts.push(part);
    }
    return parts;
}
const LOOKS_WORDS = /\b(?:looks?\s+like|(?:good|nice|bad)[\s-]+looking|idiot|idiots|stupid|dumb|ugly|pretty|handsome|beautiful|attractive|cute|sexy|fat|skinny|obese|tall|height|weight|complexion|skin\s+(?:colou?r|tone))\b/i;
const PERSON_WORDS = /\b(?:age|aged|young|younger|youngest|old|older|elderly|years?\s+old|born|birthday|gender|sex|female|females|male|males|woman|women|man|men|girl|girls|boy|boys|lady|ladies|religion|religious|muslim|muslims|hindu|hindus|christian|christians|buddhist|buddhists|caste|married|unmarried|marital|divorced|widow|widowed|spouse|husband|wife|pregnant|pregnancy|children|kids|disabled|disability|handicapped|race|racial|ethnic|ethnicity|tribal)\b/i;
/** Null when Jev may be asked; otherwise why not. Looks first, so "looks like an idiot" says "how they look". */
function unfairAskKind(text) {
    if (LOOKS_WORDS.test(text))
        return 'looks';
    if (PERSON_WORDS.test(text))
        return 'person';
    return null;
}
exports.UNFAIR_ASK_TITLES = {
    looks: 'Jev doesn’t rank people on how they look.',
    person: 'Jev doesn’t rank people on who they are.',
};
exports.UNFAIR_ASK_BODY = 'It only reads what the CV says about the work — never age, gender, religion, family or looks — so every order stays fair and explainable. Looking for weak CVs? Try one of these:';
/**
 * Asks Jev can judge from a CV's text, offered when an ask is refused. No gaps or "dates that don't add up": Jev
 * can't compare dates, and gaps count against people who took time off (ADR 0050 §6).
 */
exports.RED_FLAG_ASKS = [
    'Many spelling or grammar mistakes',
    'Duties are vague, with no results',
    'Little experience in this kind of work',
    'A list of skills with no work history',
];
// ---- Evidence and the order (ADR 0051 §4–§5) ----
/** Jev's five grades, lowest first — the `score` levels it is asked, in this order. */
exports.JEV_EVIDENCE = ['none', 'named', 'related', 'shown', 'strong'];
exports.JEV_EVIDENCE_LABELS = {
    none: 'Not shown',
    named: 'Only named',
    related: 'Related',
    shown: 'Shown in real work',
    strong: 'Strong proof',
};
/** Credit per grade (DECISIONS-LOG 2026-10-18 Q1): a skill only named counts a fifth of real use. */
exports.JEV_EVIDENCE_CREDIT = { none: 0, named: 0.2, related: 0.5, shown: 0.85, strong: 1 };
/** A must-have is met when Jev puts at least this much probability on Related or better: a listed skill never meets one. */
exports.JEV_MEETS_AT = 0.5;
exports.JEV_MUST_WEIGHT = 2;
exports.JEV_BETTER_WEIGHT = 1;
/** The grade nearest Jev's probability-weighted level (0–4). */
function jevEvidenceOf(level) {
    const index = Math.min(exports.JEV_EVIDENCE.length - 1, Math.max(0, Math.round(level)));
    return exports.JEV_EVIDENCE[index] ?? 'none';
}
/** A grading from Jev's probabilities over the five grades, lowest first. Probabilities that don't sum to 1 are scaled. */
function jevGradingOf(probabilities) {
    const clean = exports.JEV_EVIDENCE.map((_, index) => Math.max(0, probabilities[index] ?? 0));
    const total = clean.reduce((sum, value) => sum + value, 0);
    if (total === 0)
        return { level: 0, credit: 0, meets: 0 };
    const share = clean.map((value) => value / total);
    const round = (value) => Math.round(value * 1000) / 1000;
    return {
        level: round(share.reduce((sum, value, index) => sum + value * index, 0)),
        credit: round(share.reduce((sum, value, index) => sum + value * exports.JEV_EVIDENCE_CREDIT[exports.JEV_EVIDENCE[index] ?? 'none'], 0)),
        meets: round((share[2] ?? 0) + (share[3] ?? 0) + (share[4] ?? 0)),
    };
}
exports.JEV_LEVEL_LABELS = { strong: 'Strong', partial: 'Partial', weak: 'Weak' };
function jevLevelOf(match) {
    if (match >= 75)
        return 'strong';
    if (match >= 50)
        return 'partial';
    return 'weak';
}
/** `100 × Σ(weight × credit) / Σ weight`, must-haves weighing 2 and better-ifs 1, rounded. */
function jevMatchOf(answers) {
    let weighted = 0;
    let total = 0;
    for (const answer of answers) {
        const weight = answer.isMust ? exports.JEV_MUST_WEIGHT : exports.JEV_BETTER_WEIGHT;
        weighted += weight * answer.credit;
        total += weight;
    }
    return total === 0 ? 0 : Math.round((100 * weighted) / total);
}
/** Every must-have met. An ask with no must-haves is met by everyone. */
function showsAllMusts(answers) {
    return answers.every((answer) => !answer.isMust || answer.meets >= exports.JEV_MEETS_AT);
}
/** ADR 0058: a part is something the CV says when Jev's grade sits at or above 0.5, nearer Only named than Not shown — the line above which quotes are already kept. */
exports.JEV_ASSESSABLE_LEVEL = 0.5;
/**
 * ADR 0058 §A. Musts weigh 2 and Better-ifs 1, as in `jevMatchOf`. Only parts graded at or above JEV_ASSESSABLE_LEVEL are
 * assessed: `fit` is their credit share and `coverage` their weight share of the whole ask. The stored match stays as
 * it was; it is roughly fit × coverage, which is why it already stops high fit on little evidence from winning.
 */
function jevEvidenceSummaryOf(parts, answers) {
    let total = 0;
    let assessed = 0;
    let credited = 0;
    parts.forEach((part, index) => {
        const weight = part.isMust ? exports.JEV_MUST_WEIGHT : exports.JEV_BETTER_WEIGHT;
        total += weight;
        const answer = answers[index];
        if (!answer || answer.level < exports.JEV_ASSESSABLE_LEVEL)
            return;
        assessed += weight;
        credited += weight * answer.credit;
    });
    return {
        fit: assessed === 0 ? null : Math.round((100 * credited) / assessed),
        coverage: total === 0 ? 0 : Math.round((100 * assessed) / total),
    };
}
/** ADR 0058 §B: how much of the strongest evidence the person did themselves, cautious answer first (Jev leans to the first option). */
exports.JEV_OWNERSHIP = ['not_stated', 'contributor', 'component_owner', 'project_lead'];
exports.JEV_OWNERSHIP_LABELS = {
    not_stated: 'Own part not stated',
    contributor: 'Did part of it',
    component_owner: 'Owned a part',
    project_lead: 'Led it',
};
/** ADR 0058 §C–§D: at or above this share of Jev's "yes", details are missing, or the evidence is a close equivalent. */
exports.JEV_GAP_AT = 0.5;
exports.JEV_TRANSFERABLE_AT = 0.5;
/** ADR 0058 §D: Jev rates the English the CV is written in on four steps, lowest first. Shown only: it never moves anyone (owner, 2026-10-25, Q3). */
exports.JEV_ENGLISH = ['many_mistakes', 'some_mistakes', 'few_mistakes', 'clear'];
exports.JEV_ENGLISH_LABELS = {
    many_mistakes: 'Many mistakes',
    some_mistakes: 'Several mistakes',
    few_mistakes: 'A few small mistakes',
    clear: 'Clear and correct',
};
/** The list shows a chip for the two worst steps only. */
exports.JEV_ENGLISH_CHIPS = {
    many_mistakes: 'English: many mistakes',
    some_mistakes: 'English: some mistakes',
};
/** The step nearest Jev's probability-weighted level (0–3). */
function jevEnglishOf(level) {
    const index = Math.min(exports.JEV_ENGLISH.length - 1, Math.max(0, Math.round(level)));
    return exports.JEV_ENGLISH[index] ?? 'many_mistakes';
}
/** ADR 0058 §C: a part with a gap — only named, not shown, or Jev found important details missing. Older answers hold no `detailsMissing`, so the grade alone decides. A part Jev gave no answer for has one too. */
function jevGapOf(answer) {
    if (!answer)
        return true;
    const evidence = jevEvidenceOf(answer.level);
    return evidence === 'none' || evidence === 'named' || (answer.detailsMissing ?? 0) >= exports.JEV_GAP_AT;
}
function hasJevFlags(flags) {
    return !!flags && (flags.hiddenText !== null || flags.aiLines.length > 0 || flags.copiedLines > 0 || flags.isKeywordList);
}
// ---- Recruiters correct Jev's findings (ADR 0059, F23.19) ----
/** ADR 0059: why a recruiter disagrees (or agrees) with one finding. Eight plain reasons (DECISIONS-LOG 2026-10-25 Q4). */
exports.JEV_CORRECTION_CODES = [
    'CONFIRMED',
    'WRONG_GRADE',
    'QUOTE_WRONG',
    'MISSED_EVIDENCE',
    'TRANSFERABLE_NOT_RECOGNISED',
    'OWNERSHIP_OVERSTATED',
    'FALSE_FLAG',
    'POTENTIAL_FAIRNESS_ISSUE',
];
exports.JEV_CORRECTION_LABELS = {
    CONFIRMED: 'Jev got this right',
    WRONG_GRADE: 'The grade is wrong',
    QUOTE_WRONG: 'The quoted line doesn’t show this',
    MISSED_EVIDENCE: 'Jev missed evidence in the CV',
    TRANSFERABLE_NOT_RECOGNISED: 'An equivalent skill wasn’t counted',
    OWNERSHIP_OVERSTATED: 'Their own part is overstated',
    FALSE_FLAG: 'This flag is wrong',
    POTENTIAL_FAIRNESS_ISSUE: 'This looks unfair to the person',
};
/** What can be said about one part of the ask: everything except a wrong flag. */
exports.JEV_PART_CORRECTION_CODES = exports.JEV_CORRECTION_CODES.filter((code) => code !== 'FALSE_FLAG');
/** What can be said about the flags box (a null part). */
exports.JEV_FLAG_CORRECTION_CODES = ['CONFIRMED', 'FALSE_FLAG', 'POTENTIAL_FAIRNESS_ISSUE'];
exports.JEV_CORRECTION_NOTE_MAX = 500;
const JEV_LEVEL_TOP = exports.JEV_EVIDENCE.length - 1;
/** `partIndex` null is the flags box. The note is optional; an empty one is no note. */
exports.correctJevSchema = zod_1.z
    .object({
    partIndex: zod_1.z.number().int().min(0).max(exports.JEV_ICP_PARTS_MAX - 1).nullable(),
    code: zod_1.z.enum(exports.JEV_CORRECTION_CODES),
    correctedLevel: zod_1.z.number().int().min(0).max(JEV_LEVEL_TOP).nullable().default(null),
    note: zod_1.z
        .string()
        .trim()
        .max(exports.JEV_CORRECTION_NOTE_MAX, `Keep the note under ${exports.JEV_CORRECTION_NOTE_MAX} characters.`)
        .nullable()
        .default(null)
        .transform((note) => (note === '' ? null : note)),
})
    .superRefine((input, context) => {
    if (input.code === 'WRONG_GRADE' && input.correctedLevel === null)
        context.addIssue({ code: 'custom', path: ['correctedLevel'], message: 'Pick the right grade.' });
    if (input.code !== 'WRONG_GRADE' && input.correctedLevel !== null)
        context.addIssue({ code: 'custom', path: ['correctedLevel'], message: 'Only a wrong grade takes a new grade.' });
    const allowed = input.partIndex === null ? exports.JEV_FLAG_CORRECTION_CODES : exports.JEV_PART_CORRECTION_CODES;
    if (!allowed.includes(input.code))
        context.addIssue({ code: 'custom', path: ['code'], message: 'That correction doesn’t fit there.' });
});
exports.JEV_EVENTS = {
    /** F23.15: someone asked Jev to order a job's candidates. */
    ASK_RUN: 'jev.ask_run',
    /** F23.15: the ask finished, or stopped, with its counts. */
    ASK_FINISHED: 'jev.ask_finished',
    /** F23.17: Haiku wrote reasons for an ask's people — the top 50 at once, or one person when opened. */
    REASONS_WRITTEN: 'jev.reasons_written',
    /** F23.19: a recruiter said what Jev got right or wrong about one finding. */
    FINDING_CORRECTED: 'jev.finding_corrected',
    /** F23.19: that correction was taken back (Undo). The row stays; it is only marked withdrawn. */
    CORRECTION_WITHDRAWN: 'jev.correction_withdrawn',
};
/** Jev order: people read before people not read, every must-have met first, then the match. 0 keeps the criteria's order. */
function compareJevResults(a, b) {
    const aRead = a?.status === 'read';
    const bRead = b?.status === 'read';
    if (aRead !== bRead)
        return aRead ? -1 : 1;
    if (!a || !b || !aRead)
        return 0;
    if (a.showsAllMusts !== b.showsAllMusts)
        return a.showsAllMusts ? -1 : 1;
    return (b.match ?? 0) - (a.match ?? 0);
}
/** TypeSafe's list price for Jev input (docs.typesafe.ai/models, 2026-10-17): $0.042 per million tokens, output free. */
exports.JEV_MICRO_USD_PER_THOUSAND_TOKENS = 42;
/** Claude Haiku 5.5's list price (2026-10-18): $0.10 per million input tokens, $0.50 per million output. */
exports.HAIKU_MICRO_USD_PER_THOUSAND_INPUT = 100;
exports.HAIKU_MICRO_USD_PER_THOUSAND_OUTPUT = 500;
const ONE_CENT_IN_MICRO_USD = 10_000;
const MICRO_USD_PER_DOLLAR = 1_000_000;
const CENTS_PER_DOLLAR = 100;
const TOKENS_PER_THOUSAND = 1000;
function askMicroUsdOf(inputTokens, reasonInputTokens, reasonOutputTokens) {
    return Math.round((inputTokens * exports.JEV_MICRO_USD_PER_THOUSAND_TOKENS + reasonInputTokens * exports.HAIKU_MICRO_USD_PER_THOUSAND_INPUT + reasonOutputTokens * exports.HAIKU_MICRO_USD_PER_THOUSAND_OUTPUT) / TOKENS_PER_THOUSAND);
}
/** What an ask cost, in words, from the tokens Jev and Haiku reported — worked out, never stored (#10). */
function jevCostLabel(inputTokens, reasonInputTokens = 0, reasonOutputTokens = 0) {
    const microUsd = askMicroUsdOf(inputTokens, reasonInputTokens, reasonOutputTokens);
    if (microUsd < ONE_CENT_IN_MICRO_USD)
        return 'under 1 cent';
    return `about $${(microUsd / MICRO_USD_PER_DOLLAR).toFixed(2)}`;
}
/** What a top-up cost, to a tenth of a cent, for the card's "Read 40 new CVs · 3.1¢" (ADR 0062, mockup M2); dollars from a dollar up. */
function jevCentsLabel(inputTokens, reasonInputTokens = 0, reasonOutputTokens = 0) {
    const cents = askMicroUsdOf(inputTokens, reasonInputTokens, reasonOutputTokens) / ONE_CENT_IN_MICRO_USD;
    if (cents >= CENTS_PER_DOLLAR)
        return `$${(cents / CENTS_PER_DOLLAR).toFixed(2)}`;
    if (cents < 0.1)
        return 'under 0.1¢';
    return `${cents.toFixed(1)}¢`;
}
//# sourceMappingURL=jev.js.map